import type { MessageAssigneeType } from 'types/chat/messages/common';
import type { ApplicationUser } from 'types/company/applicationUser';
import type { CurrentUser } from 'types/user';

export function formatAgentDisplayName(
  firstName?: string | null,
  lastName?: string | null,
  fullName?: string | null,
): string {
  const fromParts = `${firstName || ''} ${lastName || ''}`.trim();
  return fromParts || `${fullName || ''}`.trim();
}

/** שדות שולח להודעה יוצאת אופטימית — שם + תמונה מהמשתמש המחובר. */
export function buildOutgoingSenderFieldsFromUser(user: CurrentUser | null | undefined): {
  GlobalUserId: string | null;
  GlobalUserName: string;
  GlobalUserImage: string | null;
} {
  const udid = user?.Udid ?? null;
  return {
    GlobalUserId: udid,
    GlobalUserName: formatAgentDisplayName(user?.FirstName, user?.LastName, user?.FullName),
    GlobalUserImage: user?.Image ?? null,
  };
}

function toMessageAssignee(
  source: {
    Udid?: string | null;
    FirstName?: string | null;
    LastName?: string | null;
    FullName?: string | null;
    Image?: string | null;
  },
  fallbackImage?: string | null,
): MessageAssigneeType {
  return {
    Udid: source.Udid || '',
    FullName: formatAgentDisplayName(source.FirstName, source.LastName, source.FullName),
    Image: source.Image || fallbackImage || null,
  } as MessageAssigneeType;
}

export function resolveMessageAssignee(params: {
  applicationUsers: ApplicationUser[];
  messageGlobalUserId?: string | null;
  messageGlobalUserName?: string | null;
  messageGlobalUserImage?: string | null;
  botName?: string | null;
  currentUser?: CurrentUser | null;
  isIncoming: boolean;
  /** Message.send_source — הודעת תפוצה בלי כותב לא מיוחסת לצופה (fallback המשתמש המחובר) */
  sendSource?: string | null;
}): MessageAssigneeType | undefined {
  const {
    applicationUsers,
    messageGlobalUserId,
    messageGlobalUserName,
    messageGlobalUserImage,
    botName,
    currentUser,
    isIncoming,
    sendSource,
  } = params;

  if (botName !== null && botName !== undefined) {
    return { Udid: 'bot', FullName: botName } as MessageAssigneeType;
  }

  if (messageGlobalUserId) {
    const appUser = applicationUsers.find((u) => u.Udid === messageGlobalUserId);
    if (appUser) {
      return toMessageAssignee(appUser, messageGlobalUserImage);
    }
  }

  if (messageGlobalUserImage || messageGlobalUserName) {
    return {
      Udid: messageGlobalUserId || '',
      FullName: messageGlobalUserName || '',
      Image: messageGlobalUserImage || null,
    } as MessageAssigneeType;
  }

  //* הודעת תפוצה בלי כותב ("ללא שינוי"/"ללא שיוך") — לא נופלים למשתמש המחובר:
  //* ההודעה תוצג עם צ'יפ "תפוצה" בלבד, בלי ייחוס שגוי לצופה.
  if (sendSource === 'broadcast' && !messageGlobalUserId) {
    return undefined;
  }

  if (!isIncoming && currentUser?.Udid) {
    const isOwnMessage = !messageGlobalUserId || messageGlobalUserId === currentUser.Udid;
    if (isOwnMessage) {
      return toMessageAssignee(currentUser, messageGlobalUserImage);
    }
  }

  return undefined;
}

/**
 * שדות השולח מתוך payload של הודעה (API / socket).
 * קודם GlobalUser* — כותב ההודעה בפועל (Message.GlobalUserId); ‏manager הוא
 * המשויך של ה-session ומשמש רק fallback להודעות בלי כותב. הסדר ההפוך גרם לבאג:
 * הערה שנכתבה ע"י נציג אחר יוחסה אחרי רענון למשויך השיחה.
 */
export function extractSenderFieldsFromMessagePayload(md: Record<string, unknown>): {
  GlobalUserId: string | null;
  GlobalUserName: string | null;
  GlobalUserImage: string | null;
} {
  const manager = md.manager as { id?: string; fullName?: string } | null | undefined;
  return {
    GlobalUserId: (md.GlobalUserId ?? md.globalUserId ?? manager?.id ?? null) as string | null,
    GlobalUserName: (md.GlobalUserName ?? md.globalUserName ?? manager?.fullName ?? null) as string | null,
    GlobalUserImage: (md.GlobalUserImage ?? md.globalUserImage ?? null) as string | null,
  };
}
